/**
 * Single source of truth for the member name shown in the UI.
 * Preference: displayName -> name -> legalName -> "Unknown".
 *
 * Composition rule: the PadSplit harvest often writes a preferred FIRST
 * name into display_name ("Kay") while name / legal_name hold the full
 * name ("Krisha Lewis"). When display_name is a single word and name has
 * more tokens to offer, show `display_name + rest-of-name` ("Kay Lewis")
 * instead of the bare first name. Multi-word display names render
 * verbatim — prod rows exist where the display name deliberately carries
 * a DIFFERENT surname than name (e.g. "Ree Maakheru" on "Tyree Cooper"),
 * so composing across a multi-word display name would fabricate names.
 */
export function getMemberDisplayName(
  m: { displayName?: string | null; name?: string | null; legalName?: string | null } | null | undefined,
): string {
  if (!m) return "Unknown";
  const display = m.displayName?.trim();
  const name = m.name?.trim();
  if (display) {
    if (name && !/\s/.test(display)) {
      const nameTokens = name.split(/\s+/);
      if (nameTokens.length >= 2) {
        return [display, ...nameTokens.slice(1)].join(" ");
      }
    }
    return display;
  }
  if (name) return name;
  const legal = m.legalName?.trim();
  if (legal) return legal;
  return "Unknown";
}

/**
 * The member rating to display, or null when there is none.
 *
 * Source of truth is `members.rating`, which the warm-browser harvest is being
 * wired to populate from PadSplit's `occupancy.memberScoreForOccupancy`.
 *
 * `members.average_rating` is deliberately NOT a fallback. Nothing in the app
 * has ever written that column (the reviews page computes its own average and
 * stores it elsewhere), it is populated only on a handful of legacy demo-shaped
 * rows, and on every one of those it equals `rating` — so ignoring it is a
 * no-op on current data while preventing a stale legacy score from masking a
 * genuinely absent rating once real ratings start arriving.
 *
 * Returns null for absent, blank, unparseable, and out-of-range values rather
 * than coercing to 0, so callers can render "no rating" instead of a bogus
 * zero-star score.
 */
export function getMemberRating(
  m: { rating?: string | number | null } | null | undefined,
): number | null {
  if (!m) return null;
  const raw = m.rating;
  if (raw === null || raw === undefined || raw === "") return null;
  const n = typeof raw === "number" ? raw : parseFloat(String(raw));
  if (!Number.isFinite(n)) return null;
  // PadSplit scores are a 0-5 scale. Anything outside it is corrupt data, not a
  // rating worth showing.
  if (n < 0 || n > 5) return null;
  return n;
}
